import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Volume2, VolumeX } from 'lucide-react';
import QuantumSoundscape from '../utils/QuantumSoundscape';

const IntroPage = () => {
  const navigate = useNavigate();
  const [fadeIn, setFadeIn] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVoiceEnabled, setIsVoiceEnabled] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isVoicePlaying, setIsVoicePlaying] = useState(false);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const soundscapeRef = useRef(null);
  const voiceRef = useRef(null);
  const canvasRef = useRef(null);

  // Initialize soundscape instance
  useEffect(() => {
    soundscapeRef.current = new QuantumSoundscape();
    return () => {
      if (soundscapeRef.current) {
        soundscapeRef.current.stop();
      }
    };
  }, []);

  // Play voice-over and quantum music when user first interacts
  const playVoiceOver = () => {
    if (!hasInteracted) {
      setHasInteracted(true);

      // Start the quantum soundscape music
      if (soundscapeRef.current) {
        soundscapeRef.current.start();
        setIsMusicPlaying(true);
      }

      // Play voice-over with slight delay so music sets the mood first
      if (voiceRef.current) {
        setTimeout(() => {
          voiceRef.current.volume = 0.85;
          voiceRef.current.play()
            .then(() => {
              setIsVoicePlaying(true);
              // Lower music volume while voice plays
              if (soundscapeRef.current) {
                soundscapeRef.current.setVolume(0.25);
              }
            })
            .catch(err => {
              console.log('Voice-over play failed:', err);
            });
        }, 1500);
      }
    }
  };

  useEffect(() => {
    setFadeIn(true);

    // Add event listener for voice-over end
    const voiceElement = voiceRef.current;
    if (voiceElement) {
      voiceElement.addEventListener('ended', () => {
        setIsVoicePlaying(false);
        // Restore music volume after voice ends
        if (soundscapeRef.current && !isMuted) {
          soundscapeRef.current.setVolume(0.7);
        }
      });
    }

    // Create quantum particle animation
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles = [];
    const particleCount = window.innerWidth < 768 ? 60 : 100;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    class Particle {
      constructor() {
        this.angle = Math.random() * Math.PI * 2;
        this.radius = Math.random() * 300 + 50;
        this.speed = Math.random() * 0.002 + 0.001;
        this.size = Math.random() * 2 + 1;
        this.opacity = Math.random() * 0.5 + 0.3;
        this.color = Math.random() > 0.5 ? '#a855f7' : '#c084fc';
      }

      update() {
        this.angle += this.speed;
        this.x = centerX + Math.cos(this.angle) * this.radius;
        this.y = centerY + Math.sin(this.angle) * this.radius;
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.globalAlpha = this.opacity;
        ctx.fill();
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    let animId;
    function animate() {
      ctx.fillStyle = 'rgba(0, 5, 15, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      animId = requestAnimationFrame(animate);
    }

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animId) cancelAnimationFrame(animId);
      if (voiceElement) {
        voiceElement.removeEventListener('ended', () => {});
      }
    };
  }, []);

  const toggleMute = (e) => {
    e.stopPropagation();
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    if (soundscapeRef.current) {
      if (newMuted) {
        soundscapeRef.current.mute();
      } else {
        soundscapeRef.current.unmute();
      }
    }
  };

  const toggleVoice = (e) => {
    e.stopPropagation();
    if (voiceRef.current) {
      if (isVoicePlaying) {
        voiceRef.current.pause();
        setIsVoicePlaying(false);
        // Restore music volume
        if (soundscapeRef.current && !isMuted) {
          soundscapeRef.current.setVolume(0.7);
        }
      } else {
        voiceRef.current.play().then(() => {
          setIsVoicePlaying(true);
          // Lower music while voice plays
          if (soundscapeRef.current) {
            soundscapeRef.current.setVolume(0.25);
          }
        });
      }
      setIsVoiceEnabled(!isVoiceEnabled);
    }
  };

  const handlePageClick = () => {
    if (!hasInteracted) {
      playVoiceOver();
      return;
    }
    handleEnter();
  };

  const handleEnter = () => {
    // Stop soundscape and voice when entering site
    if (soundscapeRef.current) {
      soundscapeRef.current.stop();
    }
    if (voiceRef.current) {
      voiceRef.current.pause();
      voiceRef.current.currentTime = 0;
    }
    navigate('/home');
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden cursor-pointer group bg-black"
      onClick={handlePageClick}
      data-testid="intro-page"
    >
      {/* Professional Voice-over */}
      <audio ref={voiceRef}>
        <source src={`${process.env.REACT_APP_BACKEND_URL}/api/voiceover/welcome`} type="audio/mpeg" />
      </audio>

      {/* Audio Controls */}
      <div className="absolute top-6 right-6 z-50 flex gap-3">
        {/* Voice-over Toggle */}
        <button
          onClick={toggleVoice}
          className="bg-pink-500/20 backdrop-blur-md border border-pink-400/30 rounded-full p-3 hover:bg-pink-500/30 transition-all"
          data-testid="voice-toggle-btn"
          style={{boxShadow: '0 0 20px rgba(236, 72, 153, 0.3)'}}
          title={isVoiceEnabled ? "Voice-over On" : "Voice-over Off"}
        >
          <svg className="w-6 h-6 text-pink-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isVoiceEnabled ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
            )}
          </svg>
        </button>

        {/* Music Mute/Unmute Button */}
        <button
          onClick={toggleMute}
          className="bg-purple-500/20 backdrop-blur-md border border-purple-400/30 rounded-full p-3 hover:bg-purple-500/30 transition-all"
          data-testid="mute-toggle-btn"
          style={{boxShadow: '0 0 20px rgba(168, 85, 247, 0.3)'}}
          title={isMuted ? "Quantum Music Muted" : "Quantum Music Playing"}
        >
          {isMuted ? (
            <VolumeX className="w-6 h-6 text-purple-300" />
          ) : (
            <Volume2 className="w-6 h-6 text-purple-300" />
          )}
        </button>
      </div>

      {/* Deep space gradient overlay - Purple theme */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-950/80 via-violet-950/60 to-indigo-950/90"></div>

      {/* Animated particle canvas */}
      <canvas 
        ref={canvasRef} 
        className="absolute inset-0 pointer-events-none"
        style={{mixBlendMode: 'screen'}}
      />

      {/* Circuit board pattern overlay - Purple theme */}
      <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
        backgroundImage: `
          linear-gradient(rgba(168, 85, 247, 0.4) 1px, transparent 1px),
          linear-gradient(90deg, rgba(168, 85, 247, 0.4) 1px, transparent 1px),
          linear-gradient(rgba(139, 92, 246, 0.2) 1px, transparent 1px),
          linear-gradient(90deg, rgba(139, 92, 246, 0.2) 1px, transparent 1px)
        `,
        backgroundSize: '100px 100px, 100px 100px, 20px 20px, 20px 20px',
        backgroundPosition: '0 0, 0 0, 10px 10px, 10px 10px'
      }}></div>

      {/* Central quantum eye/vortex - Purple theme */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96">
        {/* Outer glow rings */}
        <div className="absolute inset-0 rounded-full border-2 border-purple-400/30 animate-pulse" style={{animationDuration: '3s'}}></div>
        <div className="absolute inset-8 rounded-full border-2 border-purple-300/40 animate-pulse" style={{animationDuration: '2.5s', animationDelay: '0.5s'}}></div>
        <div className="absolute inset-16 rounded-full border border-violet-400/50 animate-pulse" style={{animationDuration: '2s', animationDelay: '1s'}}></div>
        
        {/* Inner glowing eye */}
        <div className="absolute inset-0 bg-gradient-radial from-purple-500/20 via-violet-600/30 to-transparent rounded-full blur-3xl animate-pulse" style={{animationDuration: '4s'}}></div>
        <div className="absolute inset-24 bg-gradient-radial from-purple-400/40 via-violet-500/50 to-transparent rounded-full blur-2xl"></div>
        
        {/* Center pupil */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24">
          <div className="absolute inset-0 bg-black rounded-full border-4 border-purple-400/60" style={{boxShadow: '0 0 40px rgba(168, 85, 247, 0.6), inset 0 0 20px rgba(168, 85, 247, 0.3)'}}></div>
          <div className="absolute inset-4 bg-gradient-to-br from-purple-600 to-violet-800 rounded-full blur-md"></div>
        </div>
      </div>

      {/* Radiating light beams - Purple theme */}
      <div className="absolute inset-0 opacity-30 pointer-events-none overflow-hidden">
        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
          <div
            key={i}
            className="absolute top-1/2 left-1/2 w-2 h-96 bg-gradient-to-t from-purple-400/60 via-purple-300/30 to-transparent animate-pulse"
            style={{
              transform: `translate(-50%, -50%) rotate(${angle}deg)`,
              transformOrigin: 'center bottom',
              animationDuration: '3s',
              animationDelay: `${i * 0.2}s`
            }}
          />
        ))}
      </div>

      {/* Scattered glowing particles - Purple theme */}
      <div className="absolute inset-0 opacity-70">
        {[...Array(50)].map((_, i) => {
          const size = Math.random() * 3 + 1;
          const colors = ['bg-purple-400', 'bg-violet-400', 'bg-pink-400'];
          const randomColor = colors[Math.floor(Math.random() * colors.length)];
          return (
            <div
              key={i}
              className={`absolute ${randomColor} rounded-full animate-pulse`}
              style={{
                width: `${size}px`,
                height: `${size}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                boxShadow: `0 0 ${size * 5}px rgba(168, 85, 247, 0.8)`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${Math.random() * 2 + 2}s`
              }}
            />
          );
        })}
      </div>

      {/* Content */}
      <div className={`relative z-20 min-h-screen flex flex-col items-center justify-center px-6 transition-all duration-2000 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        {/* Logo and Title */}
        <div className="relative text-center space-y-8 mb-12 mt-32">
          <h1 
            className="text-6xl lg:text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-pink-200 to-purple-300 mb-6 tracking-wide"
            style={{
              fontFamily: 'Cormorant Garamond, serif',
              letterSpacing: '0.08em',
              textShadow: '0 0 40px rgba(168, 85, 247, 0.9), 0 0 80px rgba(139, 92, 246, 0.6), 0 0 120px rgba(139, 92, 246, 0.3)'
            }}
          >
            Divine Quanta
          </h1>
          
          <div className="space-y-6">
            <p className="text-3xl lg:text-4xl font-semibold text-white drop-shadow-2xl" style={{fontFamily: 'Cormorant Garamond, serif', letterSpacing: '0.03em', textShadow: '0 0 30px rgba(168, 85, 247, 0.8)'}}>
              New Quantum Physics Innovation
            </p>
            <p className="text-lg lg:text-xl text-purple-100 drop-shadow-lg italic leading-relaxed max-w-3xl mx-auto" style={{fontFamily: 'Cormorant Garamond, serif'}}>
              "Revolution in Wellness & DNA Healing<br/>
              Through Distance - Anywhere in the World"
            </p>
            <p className="text-base lg:text-lg text-pink-200 drop-shadow-lg">
              Heal Mind, Body & Soul from Home
            </p>
          </div>
        </div>

        {/* Divine Light Effect */}
        <div className="relative mb-16">
          <div className="absolute inset-0 blur-3xl bg-gradient-to-r from-purple-400/40 via-violet-500/40 to-purple-400/40 rounded-full animate-pulse"></div>
          <div className="relative bg-purple-500/10 backdrop-blur-md border-2 border-purple-400/50 rounded-full px-16 py-5 shadow-2xl" style={{boxShadow: '0 0 40px rgba(168, 85, 247, 0.4)'}}>
            <p className="text-purple-100 text-xl font-light tracking-widest drop-shadow-lg">
              {isVoicePlaying ? '🔊 Playing Welcome Message...' : isMusicPlaying && !isMuted ? '🎵 Quantum Soundscape Active' : '✨ Welcome to Your Healing Journey ✨'}
            </p>
          </div>
        </div>

        {/* Enter Button with enhanced glow */}
        <div className="relative group mb-12">
          <div className="absolute inset-0 blur-2xl bg-gradient-to-r from-purple-400 via-pink-500 to-purple-400 rounded-full opacity-70 group-hover:opacity-100 transition-opacity animate-pulse"></div>
          <button
            data-testid="enter-site-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (!hasInteracted) {
                playVoiceOver();
              } else {
                handleEnter();
              }
            }}
            className="relative bg-gradient-to-r from-purple-600 via-violet-600 to-purple-600 text-white px-20 py-7 rounded-full text-2xl font-bold shadow-2xl transform hover:scale-110 transition-all duration-300 border-2 border-purple-400/60"
            style={{
              textShadow: '0 2px 8px rgba(0,0,0,0.5)',
              boxShadow: '0 0 50px rgba(168, 85, 247, 0.8), 0 0 100px rgba(139, 92, 246, 0.6), inset 0 0 30px rgba(255,255,255,0.2)'
            }}
          >
            {hasInteracted ? 'Enter' : '🔊 Play Welcome'}
          </button>
        </div>

        {/* Tap anywhere hint */}
        <p className="text-purple-300/70 text-base mt-8 animate-pulse drop-shadow-lg" style={{textShadow: '0 0 10px rgba(168, 85, 247, 0.5)'}}>
          {hasInteracted ? '✦ Click to Enter Site ✦' : '✦ Click to hear welcome message ✦'}
        </p>
      </div>

      {/* Decorative vignette with purple tint */}
      <div className="absolute bottom-0 left-0 w-full h-48 bg-gradient-to-t from-black via-purple-950/30 to-transparent pointer-events-none"></div>
      <div className="absolute top-0 left-0 w-full h-48 bg-gradient-to-b from-black/70 via-violet-950/20 to-transparent pointer-events-none"></div>
    </div>
  );
};

export default IntroPage;
